import { CommonModule } from "@angular/common";
import { Component, OnInit, inject } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatNativeDateModule } from "@angular/material/core";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatTooltipModule } from "@angular/material/tooltip";
import swal from "sweetalert2";

import { InventarioIAService } from "../../inventario-ia/inventario-ia.service";
import {
  DatosFormularioIso,
  FormularioIncidenciaIsoBase,
  ModoFormularioIso,
  TextosFormularioIso,
} from "../../shared/incidencias-iso/incidencia-iso-form-base";
import { mensajeErrorHttpIso } from "../../shared/incidencias-iso/incidencias-iso.comun";
import { InventarioIA } from "../../shared/model/iso42001/inventario-ia.model";
import { IncidenciaIA } from "../../shared/model/iso42001/incidencia-ia.model";
import { AsignarResponsableIAComponent } from "../asignar-responsable-ia/asignar-responsable-ia.component";
import {
  CARGO_SEGUIMIENTO_OPTIONS,
  GRAVEDAD_OPTIONS,
  PRIORIDAD_OPTIONS,
  TIPO_EVENTO_OPTIONS,
} from "../incidencias-ia.constants";
import { IncidenciasIAService } from "../incidencias-ia.service";

export type IncidenciaIAFormMode = ModoFormularioIso;

export type IncidenciaIAFormData = DatosFormularioIso<IncidenciaIA>;

@Component({
  selector: "app-incidencia-ia-form",
  standalone: true,
  templateUrl: "./incidencia-ia-form.component.html",
  styleUrls: ["./incidencia-ia-form.component.css"],
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatDatepickerModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatNativeDateModule,
    MatSelectModule,
    MatTooltipModule,
  ],
})
export class IncidenciaIAFormComponent extends FormularioIncidenciaIsoBase<IncidenciaIA> implements OnInit {
  readonly tipoEventoOptions = TIPO_EVENTO_OPTIONS;
  readonly gravedadOptions = GRAVEDAD_OPTIONS;
  readonly prioridadOptions = PRIORIDAD_OPTIONS;
  readonly cargoSeguimientoOptions = CARGO_SEGUIMIENTO_OPTIONS;

  incidencia: IncidenciaIA = this.crearVacia();
  inventarios: InventarioIA[] = [];

  protected readonly incidenciasService = inject(IncidenciasIAService);
  protected readonly textos: TextosFormularioIso = {
    guardando: "Guardando…",
    faltanCincoPorques: "Captura al menos los primeros 5 porques.",
  };
  private readonly inventarioService = inject(InventarioIAService);
  private readonly dialog = inject(MatDialog);

  ngOnInit(): void {
    this.data = this.data || { mode: "alta" };
    this.cargarInventarios();
    if (this.data.incidencia) {
      this.incidencia = this.normalizarFechas({ ...this.data.incidencia });
      this.prepararSeguimiento();
      this.cargarPorques();
      if (this.incidencia.idIncidenciaIa) {
        this.cargarDatosComplementarios(this.incidencia.idIncidenciaIa);
      }
      return;
    }
    if (this.data.idIncidenciaIa) {
      this.cargarIncidencia(this.data.idIncidenciaIa);
      return;
    }
    this.prepararSeguimiento();
  }

  get soloLecturaCampos(): boolean {
    return this.incidencia.estadoEvento === "ATENDIDO" || this.incidencia.estadoEvento === "VOBO";
  }

  get soloConsulta(): boolean {
    return this.incidencia.estadoEvento === "VOBO";
  }

  get requiereSeguimientoCompleto(): boolean {
    return this.muestraSeguimiento && this.incidencia.estadoEvento === "PROCESO";
  }

  get titulo(): string {
    if (this.incidencia.estadoEvento === "ATENDIDO") {
      return "Revision de incidencia IA";
    }
    if (this.incidencia.estadoEvento === "VOBO") {
      return "Consulta incidencia IA";
    }
    if (this.muestraSeguimiento) {
      return "Proceso incidencia IA";
    }
    return this.esEdicion ? "Editar incidencia IA" : "Nueva incidencia IA";
  }

  guardar(): void {
    this.error = "";
    this.advertencia = "";
    if (!this.validar()) {
      return;
    }
    this.prepararCausaOrigenParaGuardar();
    this.guardando = true;

    this.solicitudGuardar().subscribe({
      next: (res) => {
        if (this.guardadoConError(res, "No fue posible guardar la incidencia IA.")) {
          return;
        }
        this.advertencia = res.advertencia || "";
        const idIncidenciaIa = this.incidencia.idIncidenciaIa || res.idGenerado;
        this.avisarGuardadoYCerrar(
          "Incidencia IA guardada",
          `Se generó la incidencia IA con folio: ${idIncidenciaIa || "-"}.`,
          idIncidenciaIa,
        );
      },
      error: (e) => {
        this.mostrarErrorGuardar(
          mensajeErrorHttpIso(e, "No fue posible guardar la incidencia IA.", {
            servicioNoEncontrado:
              "El backend no encontró el servicio de incidencias IA. Revisa que CosRest esté compilado y reiniciado con el controller actualizado.",
            respuestaNoJson: "El backend respondió una página en lugar de JSON. La ruta de incidencias IA no está llegando al controller.",
          }),
        );
        this.guardando = false;
      },
    });
  }

  onCargoSeguimientoChange(): void {
    if (!this.incidencia.responsableSeguimientoCargo) {
      this.incidencia.nombreResponsableSeguimiento = "";
    }
  }

  abrirInformacionCausaOrigen(): void {
    swal.fire({
      title: "Causa / Origen",
      html: `
        <div style="text-align:left;">
          <p>
          <b>1. ¿Porque el sistema está arrojando datos desactualizados?<b><br>
          Porque el analista no cargó los puntos del mes anterior. <b>
          <b>2. ¿Por qué el analista no cargo los puntos del mes anterior?<b><br>
          Porque nadie le enseñó cómo hacerlo. <b>
          <b>3. ¿Porque nadie le enseñó cómo hacerlo?<b><br>
          Porque nadie conoce el procedimiento y la persona que si lo conocía renunció. <b>
          <b>4. ¿Por qué nadie conoció el procedimiento?<b><br>
          Porque la anterior persona no se lo explicó a nadie y nunca se documentó. <b>
          <b>5. ¿Por qué no se explicó a nadie ni se documentó?<b><br>
          Porque no se ha estimado la probabilidad de que una persona crítica o no pueda salir de la empresa, su dificultad de reemplazo y la complejidad del procedimiento.
          </p>
          <p style="margin-top:16px;">
            Para mas información ir al sitio
            <a href="http://10.0.1.226/images/stories/Documentos-Nuevos/nuevos/ORG/INO-005_5porques.pdf" target="_blank" rel="noopener noreferrer">
              Análisis causa raíz
            </a>
          </p>
        </div>
      `,
      icon: "info",
      confirmButtonText: "Cerrar",
    });
  }

  cambiarResponsableAsignado(): void {
    if (!this.incidencia.idIncidenciaIa || this.incidencia.estadoEvento !== "PROCESO") {
      return;
    }
    const ref = this.dialog.open(AsignarResponsableIAComponent, {
      disableClose: false,
      autoFocus: true,
      width: "80vw",
      height: "75vh",
      maxWidth: "80vw",
      maxHeight: "75vh",
      panelClass: ["custom-modalbox", "incidencia-ia-dialog", "incidencia-ia-asignacion-dialog"],
      data: {
        incidencia: this.incidencia,
      },
    });
    ref.afterClosed().subscribe((result) => {
      if (result?.saved) {
        this.dialogRef.close({ refresh: true });
      }
    });
  }

  preguntaPorque(index: number): string {
    if (index === 0) {
      return `¿Por qué fue ${this.labelTipoEvento(this.incidencia.tipoEvento) || "el evento"}?`;
    }
    const anterior = this.quitarPrefijoPorque(this.porques[index - 1]) || `la respuesta ${index}`;
    return `¿Por qué ${anterior}?`;
  }

  private cargarInventarios(): void {
    this.inventarioService.consultar({ estatus: "ACTIVO" }).subscribe({
      next: (res) => {
        this.inventarios = res?.inventarios || [];
      },
      error: () => {
        this.inventarios = [];
      },
    });
  }

  private cargarIncidencia(id: number): void {
    this.cargando = true;
    this.incidenciasService.obtener(id).subscribe({
      next: (res) => {
        if (!res?.incidencia) {
          this.error = "No se encontró la incidencia IA.";
          this.cargando = false;
          return;
        }
        this.incidencia = this.normalizarFechas(res.incidencia);
        this.comentarios = res.comentarios || [];
        this.prepararSeguimiento();
        this.cargarPorques();
        this.cargando = false;
      },
      error: () => {
        this.error = "No fue posible cargar la incidencia IA.";
        this.cargando = false;
      },
    });
  }

  private validar(): boolean {
    if (this.muestraSeguimiento) {
      if (this.soloConsulta) {
        this.error = "La incidencia en Vo.Bo. solo puede consultarse.";
        return false;
      }
      if (this.soloLecturaCampos) {
        if (!this.nuevoComentario.trim()) {
          this.error = "Agrega un comentario antes de guardar.";
          return false;
        }
        return true;
      }
      if (
        this.incidencia.responsableSeguimientoCargo &&
        !this.incidencia.nombreResponsableSeguimiento?.trim()
      ) {
        this.error = "Indica el nombre del responsable del seguimiento.";
        return false;
      }
      if (this.fechaPlaneadaEsPasada()) {
        this.error = "La fecha planeada de resolución no puede ser anterior al día actual.";
        return false;
      }
      if (this.requiereSeguimientoCompleto && !this.incidencia.fechaPlaneadaResolucion) {
        this.error = "La fecha planeada de resolucion es obligatoria.";
        return false;
      }
      if (this.requiereSeguimientoCompleto && !this.incidencia.accionCorrectiva?.trim()) {
        this.error = "La accion correctiva es obligatoria.";
        return false;
      }
      if (this.requiereSeguimientoCompleto && !this.incidencia.validacionHumanaRealizada?.trim()) {
        this.error = "La validacion humana realizada es obligatoria.";
        return false;
      }
      if (!this.validarPorquesSinSaltos()) {
        return false;
      }
      if (this.requiereSeguimientoCompleto && !this.validarCincoPorques()) {
        return false;
      }
      if (this.requiereSeguimientoCompleto && !this.tieneComentarioParaGuardar()) {
        this.error = "Agrega al menos un comentario de observacion antes de guardar el seguimiento.";
        return false;
      }
      return true;
    }
    if (!this.incidencia.idInventarioIa) {
      this.error = "Selecciona la herramienta o agente IA.";
      return false;
    }
    if (!this.incidencia.proyectoSistemaActividad?.trim()) {
      this.error = "Indica el proyecto, sistema o actividad.";
      return false;
    }
    if (!this.incidencia.tipoEvento) {
      this.error = "Selecciona el tipo de evento.";
      return false;
    }
    if (!this.incidencia.descripcionEvento?.trim()) {
      this.error = "Describe el evento.";
      return false;
    }
    if (!this.incidencia.gravedad) {
      this.error = "Selecciona la gravedad.";
      return false;
    }
    if (!this.incidencia.prioridad) {
      this.error = "Selecciona la prioridad.";
      return false;
    }
    return true;
  }

  private fechaPlaneadaEsPasada(): boolean {
    if (!this.incidencia.fechaPlaneadaResolucion) {
      return false;
    }
    const fecha = this.toDate(this.incidencia.fechaPlaneadaResolucion);
    return !!fecha && this.inicioDia(fecha).getTime() < this.minFechaPlaneada.getTime();
  }

  private cargarDatosComplementarios(id: number): void {
    this.incidenciasService.obtener(id).subscribe({
      next: (res) => {
        if (res?.incidencia) {
          this.incidencia = this.normalizarFechas(res.incidencia);
          this.prepararSeguimiento();
          this.cargarPorques();
        }
        this.comentarios = res?.comentarios || [];
      },
      error: () => {
        this.comentarios = [];
      },
    });
  }

  protected normalizarPorque(value?: string): string {
    const limpio = value?.trim() || "";
    if (!limpio) {
      return "";
    }
    if (/^(por\s+que|por\s+qué|porque)\b/i.test(limpio)) {
      return limpio;
    }
    return `Porque ${limpio}`;
  }

  private tieneComentarioParaGuardar(): boolean {
    return !!this.nuevoComentario.trim() || this.comentarios.length > 0;
  }

  private quitarPrefijoPorque(value?: string): string {
    return (value || "")
      .trim()
      .replace(/^(por\s+que|por\s+qué|porque)\s+/i, "")
      .trim();
  }

  private labelTipoEvento(value?: string): string {
    return this.tipoEventoOptions.find((option) => option.value === value)?.label || value || "";
  }

  private crearVacia(): IncidenciaIA {
    return {
      idInventarioIa: null,
      estadoEvento: "ABIERTO",
      proyectoSistemaActividad: "",
      descripcionEvento: "",
      tipoEvento: "",
      riesgoIdentificado: "",
      impacto: "",
      gravedad: "",
      prioridad: "",
      causaOrigen: "",
      accionCorrectiva: "",
      validacionHumanaRealizada: "",
      responsableSeguimientoCargo: "",
      nombreResponsableSeguimiento: "",
      observacionesAdicionales: "",
    };
  }
}
